import { store } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { Encumbrance, ScriptureData, Share } from 'types';

import actionTypes from 'constants/actionTypes';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import fileTypes from 'constants/fileTypes';
import { State } from 'redux/initialState';
import documentTypes from 'constants/documentTypes';
import {
  createEncumbrance,
  updateEncumbrance,
} from 'modules/partners/redux/encumbranceActions';
import { AddEncumbranceModalContextType } from '../types/AddEncumbranceModalContextType';
import { addEncumbranceModalInitValue } from '../constants/addEncumbranceModalInitValue';
import { useGetEncumbranceTypeOptions } from '../hooks/useGetEncumbranceTypeOptions';

const AddEncumbranceModalContext =
  createContext<AddEncumbranceModalContextType>(addEncumbranceModalInitValue);

export const AddEncumbranceModalProvider: FC<{
  children: ReactNode;
  share: Share;
  encumbrance?: Encumbrance;
  modalActionType?: string;
  size?: string;
  onAddEncumbranceSuccess?: () => void;
  onEditEncumbranceSuccess?: () => void;
}> = ({
  children,
  share,
  encumbrance,
  modalActionType = actionTypes.ADD,
  onAddEncumbranceSuccess,
  onEditEncumbranceSuccess,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const {
    data: encumbranceTypeOptionsData,
    actions: encumbranceTypeOptionsActions,
  } = useGetEncumbranceTypeOptions({
    defaultEncumbranceType: encumbrance?.type || '',
  });

  const [scriptureData, setScriptureData] = useState(
    encumbrance?.scriptureData || {
      notaryName: '',
      protocolNr: '',
      notaryCity: '',
    }
  );
  const [file, setFile] = useState<File | undefined>();
  const [from, setFrom] = useState<number>(
    encumbrance?.from || share.from || 0
  );
  const [to, setTo] = useState<number>(encumbrance?.to || share.to || 0);
  const [date, setDate] = useState<string>(
    encumbrance?.date || new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(encumbrance?.endDate || '');
  const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeScriptureData = (newScriptureData: ScriptureData) => {
    setScriptureData(newScriptureData);
    setHasBeenEdited(true);
  };

  const handleChangeFile = (newFile: File | undefined) => {
    setFile(newFile);
  };

  const handleChangeFrom = (newFrom: number) => {
    setFrom(newFrom);
    setHasBeenEdited(true);
  };

  const handleChangeTo = (newTo: number) => {
    setTo(newTo);
    setHasBeenEdited(true);
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
    setHasBeenEdited(true);
  };

  const handleChangeEndDate = (newDate: ChangeEvent<HTMLInputElement>) => {
    setEndDate(newDate.target.value);
    setHasBeenEdited(true);
  };

  const buildEncumbranceData = async (): Promise<Partial<Encumbrance>> => {
    let uploadedDocument = null;

    if (file) {
      const newDocument = {
        file,
        date: new Date(date),
        size: file.size,
        name: file.name,
        author: user['_id'],
        society: actualSociety['_id'],
        fileType: fileTypes[file.type as keyof typeof fileTypes],
        category: documentTypes.SCRIPTURES,
        description: t('DocumentLinkedToEncumbrance'),
      };
      uploadedDocument = await store.dispatch(addDocument(newDocument));
    }

    return {
      type:
        encumbranceTypeOptionsData.selectedEncumbranceTypeOption?.value || '',
      scriptureData,
      scriptureDocument: uploadedDocument ? uploadedDocument._id : undefined,
      from,
      to,
      date,
      endDate,
      share: share._id,
      society: share.society,
      partner:
        typeof share.partner === 'string' ? share.partner : share.partner?._id,
    };
  };

  const buildEncumbranceUpdateData = (): Partial<Encumbrance> => ({
    scriptureData,
    from,
    to,
    date,
    endDate,
  });

  const handleAddEncumbrance = async () => {
    try {
      setIsLoading(true);

      const encumbranceData = await buildEncumbranceData();

      await store.dispatch(
        createEncumbrance({
          encumbranceData,
        })
      );

      onAddEncumbranceSuccess?.();
      successSwal(t('EncumbranceCreatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorCreatingEncumbrance'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleEditEncumbrance = async () => {
    if (!encumbrance?._id) {
      return;
    }

    try {
      setIsLoading(true);

      await store.dispatch(
        updateEncumbrance({
          encumbranceId: encumbrance._id,
          updateEncumbranceData: buildEncumbranceUpdateData(),
        })
      );

      onEditEncumbranceSuccess?.();
      successSwal(t('EncumbranceUpdatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorUpdatingEncumbrance'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleSave =
    modalActionType === actionTypes.EDIT
      ? handleEditEncumbrance
      : handleAddEncumbrance;

  return (
    <AddEncumbranceModalContext.Provider
      value={{
        data: {
          modalActionType,
          scriptureData,
          file,
          shareFrom: share.from,
          shareTo: share.to,
          from,
          to,
          date,
          endDate,
          selectedEncumbranceTypeOption:
            encumbranceTypeOptionsData.selectedEncumbranceTypeOption,
          encumbranceTypeOptions:
            encumbranceTypeOptionsData.encumbranceTypeOptions,
          hasBeenEdited,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeScriptureData,
          handleChangeFile,
          handleChangeFrom,
          handleChangeTo,
          handleChangeDate,
          handleChangeEndDate,
          handleChangeEncumbranceTypeOption:
            encumbranceTypeOptionsActions.handleChangeEncumbranceTypeOption,
          handleSave,
        },
      }}
    >
      {children}
    </AddEncumbranceModalContext.Provider>
  );
};

export const useAddEncumbranceModal = () => {
  const context = useContext(AddEncumbranceModalContext);
  if (!context) {
    throw new Error(
      'useAddEncumbranceModal must be used within a AddEncumbranceModalProvider'
    );
  }
  return context;
};
